'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getRoomsByCompany,
    deleteRoom,
    Room,
    ROOM_COLORS,
} from '@/lib/api/rooms/rooms-api'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdClose,
    MdMeetingRoom,
    MdPeople,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import ConfirmModal from '@/components/confirm-modal'

export default function RoomListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'registrations',
        'rooms'
    )
    const [rooms, setRooms] = useState<Room[]>([])
    const [filteredRooms, setFilteredRooms] = useState<Room[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [roomToDelete, setRoomToDelete] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadRooms()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const filtered = rooms.filter(
                room =>
                    room.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    room.description
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
            setFilteredRooms(filtered)
        } else {
            setFilteredRooms(rooms)
        }
    }, [searchTerm, rooms])

    const loadRooms = async () => {
        if (!user?.companyId) return

        setLoading(true)
        try {
            const response = await getRoomsByCompany(user.companyId)
            if (response.success) {
                setRooms(response.result)
                setFilteredRooms(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar salas')
            }
        } catch (error) {
            toast.error('Erro ao carregar salas')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (roomId: number) => {
        setRoomToDelete(roomId)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!roomToDelete) return

        setDeleting(true)
        try {
            const response = await deleteRoom(roomToDelete)
            if (response.success) {
                toast.success('Sala excluída com sucesso')
                loadRooms()
                setShowDeleteModal(false)
                setRoomToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir sala')
            }
        } catch (error) {
            toast.error('Erro ao excluir sala')
        } finally {
            setDeleting(false)
        }
    }

    const getColorClass = (color?: string) => {
        if (!color) return 'bg-gray-400'
        const found = ROOM_COLORS.find(c => c.value === color)
        return found ? found.class : 'bg-gray-400'
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando salas...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdMeetingRoom className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Salas e Consultórios
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {rooms.length}{' '}
                            {rooms.length === 1 ? 'sala' : 'salas'} cadastradas
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/registrations/rooms/new')}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Adicionar</span>
                    </button>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome ou descrição..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredRooms.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdMeetingRoom className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma sala encontrada'
                                : 'Nenhuma sala cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando as salas e consultórios da sua empresa'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/registrations/rooms/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeira sala
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredRooms.map(room => (
                        <div
                            key={room.id}
                            className={`group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300 ${
                                !room.active ? 'opacity-60' : ''
                            }`}
                        >
                            <div
                                className={`h-2 ${getColorClass(room.color)}`}
                            ></div>
                            <div className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="text-base font-semibold text-base-content truncate">
                                                {room.name}
                                            </h3>
                                            <span
                                                className={`badge badge-sm ${
                                                    room.active
                                                        ? 'badge-success'
                                                        : 'badge-ghost'
                                                }`}
                                            >
                                                {room.active
                                                    ? 'Ativa'
                                                    : 'Inativa'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {room.description && (
                                    <p className="text-sm text-base-content/70 mb-3 line-clamp-2">
                                        {room.description}
                                    </p>
                                )}

                                <div className="flex flex-wrap items-center gap-3 text-sm mb-4">
                                    {room.capacity && (
                                        <div className="flex items-center gap-1 text-base-content/70">
                                            <MdPeople className="text-base" />
                                            <span>
                                                {room.capacity}{' '}
                                                {room.capacity === 1
                                                    ? 'pessoa'
                                                    : 'pessoas'}
                                            </span>
                                        </div>
                                    )}
                                    {room.resources && (
                                        <span className="badge badge-outline badge-sm">
                                            {room.resources}
                                        </span>
                                    )}
                                </div>

                                {(canUpdate || canDelete) && (
                                    <div className="flex items-center gap-2 pt-3 border-t border-base-300/50">
                                        {canUpdate && (
                                            <button
                                                onClick={() =>
                                                    navigate(
                                                        `/registrations/rooms/${room.id}`
                                                    )
                                                }
                                                className="btn btn-sm btn-ghost flex-1"
                                            >
                                                <MdEdit className="text-lg" />
                                                Editar
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                onClick={() =>
                                                    handleDelete(room.id)
                                                }
                                                className="btn btn-sm btn-ghost text-error hover:bg-error/10"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setRoomToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir sala"
                message="Tem certeza de que deseja excluir esta sala? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
